import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Loader2, CheckCircle, ShoppingCart, Crown, Check, X, BookOpen, Printer, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const SUBJECTS = ["Mathematics", "Language Arts", "Science", "Social Studies"];
const GRADES = [4, 5, 6];

function calculatePrice(count: number): { total: number; perPaper: number; savings: number } {
  let perPaper: number;
  if (count >= 10) perPaper = 350;
  else if (count >= 5) perPaper = 400;
  else if (count >= 2) perPaper = 600;
  else perPaper = 800;
  const total = perPaper * count;
  const savings = (800 * count) - total;
  return { total, perPaper, savings };
}

type Paper = { id: string; title: string; grade: number; subject: string; questionCount: number; paperNumber: number };

export default function PracticePapers() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [grade, setGrade] = useState<number>(6);
  const [subject, setSubject] = useState<string>("Mathematics");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cartOpen, setCartOpen] = useState(false);
  const [showReview, setShowReview] = useState(false);

  const isSubscriber = user?.subscriptionStatus === 'active';

  // Fetch current view
  const { data: libraryData, isLoading } = useQuery({
    queryKey: ["/api/practice-papers/library", grade, subject],
    queryFn: async () => {
      const res = await fetch(`/api/practice-papers/library?grade=${grade}&subject=${encodeURIComponent(subject)}`);
      if (!res.ok) throw new Error("Failed to load papers");
      return res.json() as Promise<{ papers: Paper[] }>;
    },
  });

  // Fetch ALL papers for cart display
  const { data: allPapersData } = useQuery({
    queryKey: ["/api/practice-papers/library-all"],
    queryFn: async () => {
      const res = await fetch("/api/practice-papers/library");
      if (!res.ok) throw new Error("Failed to load papers");
      return res.json() as Promise<{ papers: Paper[] }>;
    },
  });

  const allPapersMap = new Map((allPapersData?.papers || []).map(p => [p.id, p]));
  const selectedPapers = Array.from(selected).map(id => allPapersMap.get(id)).filter(Boolean) as Paper[];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('purchase') === 'cancelled') {
      toast({ title: "Purchase cancelled", description: "No charge was made.", variant: "destructive" });
      window.history.replaceState({}, '', '/practice-papers');
    }
  }, []);

  const checkout = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/practice-papers/guest-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paperIds: Array.from(selected) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Checkout failed" }));
        throw new Error(err.error);
      }
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => { if (data.url) window.location.href = data.url; },
    onError: (err: Error) => { toast({ title: "Checkout failed", description: err.message, variant: "destructive" }); },
  });

  const downloadPaper = useMutation({
    mutationFn: async (paperId: string) => {
      const res = await fetch(`/api/practice-papers/download/${paperId}`, { method: "POST" });
      if (!res.ok) throw new Error("Download failed");
      return { blob: await res.blob(), paperId };
    },
    onSuccess: ({ blob, paperId }) => {
      const paper = libraryData?.papers.find(p => p.id === paperId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${paper?.title?.replace(/\s+/g, '_') || 'Practice_Paper'}.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      toast({ title: "Downloaded!" });
    },
    onError: () => { toast({ title: "Download failed", variant: "destructive" }); },
  });

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (!libraryData?.papers) return;
    const currentIds = libraryData.papers.map(p => p.id);
    const allCurrentSelected = currentIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allCurrentSelected) {
        currentIds.forEach(id => next.delete(id));
      } else {
        currentIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const currentViewAllSelected = libraryData?.papers?.every(p => selected.has(p.id)) && (libraryData?.papers?.length || 0) > 0;
  const pricing = calculatePrice(selected.size);

  const handleGenDownload = () => {};

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="bg-[#1a365d] text-white py-16 md:py-20 relative overflow-hidden">
        <div className="container px-4 md:px-6 max-w-5xl mx-auto text-center">
          <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#FFC209] mb-3">
            PEP Exam Preparation
          </p>
          <h1 className="font-[Montserrat] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Practice Papers{" "}
            <span className="text-[#FFC209]">Ready to Print</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
            Professionally formatted PEP-style exam papers with 40 curriculum-aligned questions, diagrams, and answer keys. All subjects, Grades 4-6.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/70">
            <span className="flex items-center gap-2"><FileText className="h-4 w-4" /> 60 Papers Available</span>
            <span className="flex items-center gap-2"><BookOpen className="h-4 w-4" /> 4 Subjects</span>
            <span className="flex items-center gap-2"><Printer className="h-4 w-4" /> A4 Print-Ready</span>
            <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Answer Key Included</span>
          </div>
        </div>
      </section>

      {/* Paper Library */}
      <section className="py-12 md:py-16">
        <div className="container px-4 md:px-6 max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-[Montserrat] text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">
              Browse Papers
            </h2>
            <p className="text-gray-500">Select the papers you want to download</p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6 justify-center">
            <Select value={grade.toString()} onValueChange={v => setGrade(parseInt(v))}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>{GRADES.map(g => <SelectItem key={g} value={g.toString()}>Grade {g}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
              <SelectContent>{SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {!isSubscriber && libraryData?.papers && libraryData.papers.length > 0 && (
              <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs text-[#0789c3]">
                {currentViewAllSelected ? "Deselect All" : "Select All"}
              </Button>
            )}
          </div>

          {/* Papers grid */}
          {isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : libraryData?.papers && libraryData.papers.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {libraryData.papers.map(paper => {
                const isSelected = selected.has(paper.id);
                return (
                  <div
                    key={paper.id}
                    onClick={() => !isSubscriber && toggle(paper.id)}
                    className={`relative border rounded-2xl p-5 transition-all ${
                      isSubscriber
                        ? "border-gray-200 bg-white"
                        : isSelected
                          ? "border-[#0789c3] bg-[#0789c3]/5 ring-2 ring-[#0789c3] cursor-pointer"
                          : "border-gray-200 bg-white hover:border-[#0789c3]/40 hover:shadow-md cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {!isSubscriber && (
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                          isSelected ? "bg-[#0789c3] border-[#0789c3]" : "border-gray-300"
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                      )}
                      <div className="p-2 rounded-xl bg-[#0789c3]/10 flex-shrink-0">
                        <FileText className="h-5 w-5 text-[#0789c3]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-[Montserrat] font-semibold text-sm text-gray-900">{paper.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{paper.questionCount} questions with answer key</p>
                      </div>
                    </div>
                    {isSubscriber && (
                      <Button
                        size="sm"
                        className="mt-3 w-full gap-1.5 bg-[#0789c3] hover:bg-[#066a99] rounded-xl"
                        onClick={(e) => { e.stopPropagation(); downloadPaper.mutate(paper.id); }}
                        disabled={downloadPaper.isPending}
                      >
                        <Download className="h-3.5 w-3.5" /> Download PDF
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No papers available for this selection yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Value Comparison */}
      {!isSubscriber && (
        <section className="py-12 md:py-16 bg-[#EBEFF4]/50">
          <div className="container px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
                Compare Options
              </p>
              <h2 className="font-[Montserrat] text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                Papers or <span className="text-[#0789c3]">Full Platform?</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {/* Individual */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                <p className="font-[Montserrat] font-bold text-lg text-gray-900 mb-1">Individual Papers</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">J$350 - $800 <span className="text-sm font-normal text-gray-500">each</span></p>
                <p className="text-sm text-gray-500 mb-5">Pay per paper, no commitment</p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> 40 PEP-style questions per paper</li>
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> Print-ready A4 format</li>
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> Answer key included</li>
                  <li className="flex items-start gap-2.5 text-gray-400"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> No interactive online quizzes</li>
                  <li className="flex items-start gap-2.5 text-gray-400"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> No progress tracking</li>
                  <li className="flex items-start gap-2.5 text-gray-400"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> No adaptive learning</li>
                  <li className="flex items-start gap-2.5 text-gray-400"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> No parent reports</li>
                </ul>
              </div>

              {/* Subscription */}
              <div className="bg-white rounded-2xl border-2 border-[#0789c3] p-6 md:p-8 relative shadow-lg">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-[#FFC209] text-gray-900 text-xs font-[Montserrat] font-bold px-4 py-1.5 rounded-full shadow-sm">BEST VALUE</span>
                </div>
                <p className="font-[Montserrat] font-bold text-lg text-gray-900 mb-1">Academy Subscription</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">J$2,500 <span className="text-sm font-normal text-gray-500">/month</span></p>
                <p className="text-sm text-[#0789c3] font-medium mb-5">That's just 4 papers worth</p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> <strong>Unlimited</strong> practice papers</li>
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> 9,000+ interactive online quizzes</li>
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> Adaptive learning that adjusts to your child</li>
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> Weekly progress reports via WhatsApp</li>
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> XP, streaks, and leaderboards</li>
                  <li className="flex items-start gap-2.5 text-gray-700"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> All subjects, Grades 4-6</li>
                </ul>
                <Button
                  className="w-full mt-6 bg-[#0789c3] hover:bg-[#066a99] rounded-full py-6 text-base font-[Montserrat] font-bold gap-2"
                  onClick={() => window.location.href = '/signup'}
                >
                  Subscribe Now <ArrowRight className="h-4 w-4" />
                </Button>
                <p className="text-xs text-center text-gray-400 mt-3">Cancel anytime. No long-term commitment.</p>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
              Paper pricing: 1 paper J$800 | 2-4 papers J$600 each | 5+ papers J$400 each | 10+ papers J$350 each
            </p>
          </div>
        </section>
      )}

      {/* Sticky cart bar for non-subscribers */}
      {!isSubscriber && selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t bg-white/95 backdrop-blur shadow-lg z-20">
          {cartOpen && (
            <div className="max-w-5xl mx-auto px-4 pt-3 max-h-[40vh] overflow-y-auto">
              <div className="space-y-1.5">
                {selectedPapers.map(paper => (
                  <div key={paper.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50 text-sm">
                    <span className="truncate flex-1 mr-2 text-gray-700">{paper.title}</span>
                    <button onClick={() => toggle(paper.id)} className="text-red-500 hover:bg-red-50 text-xs font-medium px-2 py-0.5 rounded transition-colors">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <button onClick={() => setCartOpen(!cartOpen)} className="text-left">
              <p className="font-[Montserrat] font-semibold text-sm flex items-center gap-1.5 text-gray-900">
                <ShoppingCart className="h-3.5 w-3.5" />
                {selected.size} paper{selected.size !== 1 ? 's' : ''}
                <svg className={`h-3 w-3 transition-transform ${cartOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
              </p>
              <p className="text-xs text-gray-500">
                J${pricing.total.toLocaleString()} (J${pricing.perPaper} each)
                {pricing.savings > 0 && <span className="text-green-600 ml-1">Save J${pricing.savings.toLocaleString()}</span>}
              </p>
            </button>
            <Button onClick={() => setShowReview(true)} className="gap-2 bg-[#0789c3] hover:bg-[#066a99] rounded-full">
              <ShoppingCart className="h-4 w-4" /> Review Order
            </Button>
          </div>
        </div>
      )}

      {/* Cart Review Overlay */}
      {showReview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-[Montserrat] font-bold text-lg">Review Your Order</h2>
              <button onClick={() => setShowReview(false)} className="text-gray-400 hover:text-gray-600 text-sm font-medium">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {selectedPapers.map(paper => (
                <div key={paper.id} className="flex items-center justify-between py-2 px-3 rounded-xl bg-gray-50">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-1.5 rounded-lg bg-[#0789c3]/10 flex-shrink-0">
                      <FileText className="h-3.5 w-3.5 text-[#0789c3]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{paper.title}</p>
                      <p className="text-xs text-gray-500">{paper.questionCount} questions</p>
                    </div>
                  </div>
                  <button onClick={() => toggle(paper.id)} className="text-xs font-medium text-red-500 hover:bg-red-50 px-2 py-1 rounded transition-colors flex-shrink-0">Remove</button>
                </div>
              ))}
              {selectedPapers.length === 0 && (
                <p className="text-center text-gray-400 py-8 text-sm">No papers selected.</p>
              )}
            </div>
            {selectedPapers.length > 0 && (
              <div className="border-t p-4 space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{selected.size} paper{selected.size !== 1 ? 's' : ''} x J${pricing.perPaper}</span>
                  <span className="font-medium text-gray-900">J${(pricing.perPaper * selected.size).toLocaleString()}</span>
                </div>
                {pricing.savings > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Volume discount</span>
                    <span>-J${pricing.savings.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base pt-1 border-t text-gray-900">
                  <span>Total</span>
                  <span>J${pricing.total.toLocaleString()}</span>
                </div>
                <Button
                  onClick={() => { setShowReview(false); checkout.mutate(); }}
                  disabled={checkout.isPending}
                  className="w-full gap-2 bg-[#0789c3] hover:bg-[#066a99] rounded-full py-6 font-[Montserrat] font-bold"
                  size="lg"
                >
                  {checkout.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                  Proceed to Payment
                </Button>
                <p className="text-xs text-gray-400 text-center">You'll enter your email and card details on the next page</p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
